import React, { useState, useMemo, useCallback } from "react";
import {
  Combobox,
  useCombobox,
  CloseButton,
  Text,
  Stack,
  Group,
  Button,
  Box,
  ScrollArea,
  Pill,
  PillsInput,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import Fuse, { type IFuseOptions } from "fuse.js";

import { useAppEnvironment, useAppEssentialsContext } from "@/contexts";
import { ExpenseKind, ExpenseCategory } from "@/utils/api";
import { AppIcon } from "@/components/generic";
import { ExpenseKindPickerModal } from "./ExpenseKindPickerModal";

// ============================================
// Types
// ============================================

export interface ExpenseKindMultiSelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected kind IDs */
  value: number[];

  /** Callback when selection changes: (name, kindIds) */
  onChange: (name: string, value: number[]) => void;

  /** Input label */
  label?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Input size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Error message */
  error?: string | null;

  /** Whether field is required (prevents clearing all) */
  required?: boolean;

  /** Whether field is disabled */
  disabled?: boolean;

  /** Quick access expense codes to show as shortcut buttons */
  quickAccessCodes?: string[];

  /** Maximum number of kinds that can be selected */
  maxSelections?: number;

  /** Whether to show only schedulable kinds */
  schedulableOnly?: boolean;
}

// ============================================
// Constants
// ============================================

const DEFAULT_QUICK_ACCESS_CODES = ["PARKING", "CAR_WASH_EXTERIOR", "TOLLS"];

const CATEGORY_ICONS: Record<string, string> = {
  MAINTENANCE: "CarGarage",
  REPAIRS: "Tool",
  OTHER: "Dots",
};

const KIND_ICONS: Record<string, string> = {
  parking: "Parking",
  car_wash: "Droplet",
  toll: "Road",
  oil_change: "Oil",
  tire_rotation: "Circle",
  insurance: "Shield",
  registration: "FileText",
  inspection: "ClipboardCheck",
};

const ICON_SIZES = {
  inputLeft: 20,
  inputRight: 18,
  categoryHeader: 18,
  kindOption: 20,
  maintenanceBadge: 16,
  quickAccess: 16,
  check: 16,
};

const FUSE_OPTIONS_KINDS: IFuseOptions<ExpenseKind> = {
  keys: [
    { name: "name", weight: 0.7 },
    { name: "code", weight: 0.3 },
  ],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

const FUSE_OPTIONS_CATEGORIES: IFuseOptions<ExpenseCategory> = {
  keys: [{ name: "name", weight: 1.0 }],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

// ============================================
// Helper Functions
// ============================================

const getIconForKind = (code: string | undefined): string => {
  if (!code) return "Receipt";
  return KIND_ICONS[code.toLowerCase()] || "Receipt";
};

const getIconForCategory = (code: string | undefined): string => {
  if (!code) return "Folder";
  return CATEGORY_ICONS[code.toUpperCase()] || "Folder";
};

// ============================================
// Component
// ============================================

export const ExpenseKindMultiSelector: React.FC<
  ExpenseKindMultiSelectorProps
> = ({
  name = "kindIds",
  value,
  onChange,
  label,
  placeholder,
  size = "md",
  error,
  required,
  disabled,
  quickAccessCodes = DEFAULT_QUICK_ACCESS_CODES,
  maxSelections,
  schedulableOnly,
}) => {
    const { t } = useTranslation(["expenses", "common"]);
    const { expenseCategories, expenseKinds, getExpenseKindById } =
      useAppEssentialsContext();
    const { isMobile } = useAppEnvironment();

    const [modalOpened, setModalOpened] = useState(false);

    const combobox = useCombobox({
      onDropdownClose: () => {
        combobox.resetSelectedOption();
        setSearchValue("");
      },
    });

    const [searchValue, setSearchValue] = useState("");

    const isAtMax =
      maxSelections !== undefined && value.length >= maxSelections;

    // Resolve selected kind objects from IDs
    const selectedKinds = useMemo<ExpenseKind[]>(() => {
      return value
        .map((id) => getExpenseKindById(id))
        .filter((k): k is ExpenseKind => k !== undefined);
    }, [value, getExpenseKindById]);

    // Quick access kinds
    const quickAccessKinds = useMemo(() => {
      return quickAccessCodes
        .map((code) =>
          expenseKinds.find((k) => k.code?.toUpperCase() === code.toUpperCase())
        )
        .filter((k): k is ExpenseKind => k !== undefined);
    }, [quickAccessCodes, expenseKinds]);

    // Fuse indexes
    const categoryFuse = useMemo(
      () => new Fuse(expenseCategories, FUSE_OPTIONS_CATEGORIES),
      [expenseCategories]
    );

    const kindFuseByCategory = useMemo(() => {
      const map = new Map<string | number, Fuse<ExpenseKind>>();
      for (const category of expenseCategories) {
        if (category.kinds && category.kinds.length > 0) {
          map.set(
            category.id || "",
            new Fuse(category.kinds, FUSE_OPTIONS_KINDS)
          );
        }
      }
      return map;
    }, [expenseCategories]);

    // Filtered categories for the dropdown search
    const filteredCategories = useMemo(() => {
      const search = searchValue.trim();
      if (!search) return expenseCategories;

      const matchedCategoryIds = new Set(
        categoryFuse.search(search).map((result) => result.item.id)
      );

      return expenseCategories
        .map((category) => {
          if (matchedCategoryIds.has(category.id)) return category;

          const fuse = kindFuseByCategory.get(category.id || "");
          if (!fuse) return null;

          const matchingKinds = fuse.search(search).map((result) => result.item);
          if (matchingKinds.length > 0) return { ...category, kinds: matchingKinds };

          return null;
        })
        .filter((c): c is ExpenseCategory => c !== null);
    }, [expenseCategories, searchValue, categoryFuse, kindFuseByCategory]);

    // Toggle a kind ID in the current selection
    const toggleKind = useCallback(
      (kindId: number) => {
        const isSelected = value.includes(kindId);
        if (isSelected) {
          // Don't allow deselecting the last item when required
          if (required && value.length === 1) return;
          onChange(name, value.filter((id) => id !== kindId));
        } else {
          if (isAtMax) return;
          onChange(name, [...value, kindId]);
        }
      },
      [value, name, onChange, required, isAtMax]
    );

    // Remove a pill
    const handleRemovePill = useCallback(
      (kindId: number) => {
        if (required && value.length === 1) return;
        onChange(name, value.filter((id) => id !== kindId));
      },
      [value, name, onChange, required]
    );

    // Desktop combobox option submit
    const handleOptionSubmit = useCallback(
      (kindIdStr: string) => {
        const kindId = parseInt(kindIdStr, 10);
        if (!isNaN(kindId)) {
          toggleKind(kindId);
          combobox.updateSelectedOptionIndex("active");
        }
      },
      [toggleKind, combobox]
    );

    // Quick access toggle (both mobile and desktop)
    const handleQuickToggle = useCallback(
      (kind: ExpenseKind) => {
        if (kind.id !== undefined) toggleKind(Number(kind.id));
      },
      [toggleKind]
    );

    // Clear all selections
    const handleClearAll = useCallback(() => {
      if (!required) onChange(name, []);
    }, [name, onChange, required]);

    // Search input change
    const handleSearchChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.currentTarget.value);
        combobox.updateSelectedOptionIndex();
      },
      [combobox]
    );

    // Mobile modal confirm
    const handleModalSelect = useCallback(
      (kindIds: number[]) => {
        onChange(name, kindIds);
      },
      [name, onChange]
    );

    const hasResults = filteredCategories.some(
      (c) => c.kinds && c.kinds.length > 0
    );

    const fieldLabel =
      label ?? t("expenses:form.fields.expenseKind.labelMulti", {
        defaultValue: t("expenses:form.fields.expenseKind.label"),
      });
    const fieldPlaceholder =
      placeholder ?? t("expenses:form.fields.expenseKind.placeholder");

    // ==========================================
    // Mobile: Pills display + full-screen modal
    // ==========================================
    if (isMobile) {
      return (
        <Stack gap="xs">
          {/* Trigger area — shows selected pills or placeholder */}
          <Box
            onClick={() => {
              if (!disabled) setModalOpened(true);
            }}
            style={(theme) => ({
              border: `1px solid ${error
                ? "var(--mantine-color-red-6)"
                : "var(--mantine-color-gray-4)"
                }`,
              borderRadius: theme.radius.sm,
              padding: "8px 12px",
              minHeight: 42,
              cursor: disabled ? "not-allowed" : "pointer",
              backgroundColor: disabled
                ? "var(--mantine-color-gray-0)"
                : "var(--mantine-color-body)",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 4,
            })}
          >
            {/* Label above, rendered outside for clarity */}
            {selectedKinds.length === 0 ? (
              <Text size="sm" c="dimmed">
                {fieldPlaceholder}
              </Text>
            ) : (
              <Group gap={4} wrap="wrap" style={{ flex: 1 }}>
                {selectedKinds.map((kind) => (
                  <Pill
                    key={kind.id}
                    withRemoveButton={!disabled && (!required || value.length > 1)}
                    onRemove={() => {
                      if (kind.id !== undefined) handleRemovePill(Number(kind.id));
                    }}
                    onClick={(e) => e.stopPropagation()}
                    size="sm"
                  >
                    {kind.name}
                  </Pill>
                ))}
              </Group>
            )}
            <Box ml="auto" style={{ flexShrink: 0 }}>
              <AppIcon iconName="ChevronDown" size={ICON_SIZES.inputRight} />
            </Box>
          </Box>

          {/* Label + error rendered as a separate element to match Mantine Input style */}
          {fieldLabel && (
            <Text
              component="label"
              size="sm"
              fw={500}
              style={{ order: -1, display: "block" }}
            >
              {fieldLabel}
              {required && (
                <Text component="span" c="red" ml={2}>
                  *
                </Text>
              )}
            </Text>
          )}
          {error && (
            <Text size="xs" c="red">
              {error}
            </Text>
          )}

          {/* Quick Access Buttons */}
          {quickAccessKinds.length > 0 && !disabled && (
            <Group gap="xs" wrap="wrap">
              {quickAccessKinds.map((kind) => {
                const isSelected = kind.id !== undefined && value.includes(Number(kind.id));
                const isDisabledByMax = isAtMax && !isSelected;
                return (
                  <Button
                    key={kind.id}
                    variant={isSelected ? "filled" : "light"}
                    size="sm"
                    disabled={isDisabledByMax}
                    leftSection={
                      <AppIcon
                        iconName={getIconForKind(kind.code)}
                        size={ICON_SIZES.quickAccess}
                      />
                    }
                    onClick={() => handleQuickToggle(kind)}
                  >
                    {kind.name}
                  </Button>
                );
              })}
            </Group>
          )}

          {/* Full-screen Picker Modal (multi mode) */}
          <ExpenseKindPickerModal
            multiSelect
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            value={value}
            onSelect={handleModalSelect}
            quickAccessCodes={quickAccessCodes}
            schedulableOnly={schedulableOnly}
            maxSelections={maxSelections}
          />
        </Stack>
      );
    }

    // ==========================================
    // Desktop: PillsInput + Combobox dropdown
    // ==========================================
    return (
      <Stack gap="xs">
        <Combobox
          store={combobox}
          onOptionSubmit={handleOptionSubmit}
          withinPortal
        >
          <Combobox.DropdownTarget>
            <PillsInput
              label={fieldLabel}
              error={error}
              required={required}
              disabled={disabled}
              size={size}
              onClick={() => combobox.openDropdown()}
              onFocus={() => combobox.openDropdown()}
              rightSection={
                selectedKinds.length > 0 && !required && !disabled ? (
                  <CloseButton
                    size={size}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleClearAll}
                    aria-label={t("common:form.clearAll", {
                      defaultValue: "Clear all",
                    })}
                  />
                ) : (
                  <AppIcon iconName="ChevronDown" size={ICON_SIZES.inputRight} />
                )
              }
              rightSectionPointerEvents={
                selectedKinds.length > 0 && !required && !disabled
                  ? "auto"
                  : "none"
              }
            >
              <Pill.Group>
                {selectedKinds.map((kind) => (
                  <Pill
                    key={kind.id}
                    withRemoveButton={!disabled && (!required || value.length > 1)}
                    onRemove={() => {
                      if (kind.id !== undefined) handleRemovePill(Number(kind.id));
                    }}
                  >
                    {kind.name}
                  </Pill>
                ))}

                <Combobox.EventsTarget>
                  <PillsInput.Field
                    value={searchValue}
                    onChange={handleSearchChange}
                    onBlur={() => {
                      combobox.closeDropdown();
                      setSearchValue("");
                    }}
                    onKeyDown={(event) => {
                      // Backspace on empty search removes the last pill
                      if (
                        event.key === "Backspace" &&
                        searchValue.length === 0 &&
                        selectedKinds.length > 0 &&
                        (!required || value.length > 1)
                      ) {
                        onChange(name, value.slice(0, -1));
                      }
                    }}
                    placeholder={selectedKinds.length === 0 ? fieldPlaceholder : ""}
                  />
                </Combobox.EventsTarget>
              </Pill.Group>
            </PillsInput>
          </Combobox.DropdownTarget>

          <Combobox.Dropdown>
            <ScrollArea.Autosize mah={400} type="scroll">
              <Combobox.Options>
                {!hasResults && searchValue && (
                  <Combobox.Empty>
                    {t("expenses:form.fields.expenseKind.noResults")}
                  </Combobox.Empty>
                )}

                {filteredCategories.map((category) => {
                  if (!category.kinds || category.kinds.length === 0) return null;

                  const categoryKinds = schedulableOnly
                    ? category.kinds // TODO: .filter(kind => kind.canSchedule)
                    : category.kinds;

                  return (
                    <Combobox.Group
                      key={category.id}
                      label={
                        <Group gap="xs" py={4}>
                          <AppIcon
                            iconName={getIconForCategory(category.code)}
                            size={ICON_SIZES.categoryHeader}
                          />
                          <Text size="md" fw={600} tt="uppercase">
                            {category.name}
                          </Text>
                        </Group>
                      }
                    >
                      {categoryKinds.map((kind) => {
                        const isSelected =
                          kind.id !== undefined && value.includes(Number(kind.id));
                        const isDisabledByMax = isAtMax && !isSelected;

                        return (
                          <Combobox.Option
                            key={kind.id}
                            value={String(kind.id)}
                            active={isSelected}
                            disabled={isDisabledByMax}
                            py="sm"
                          >
                            <Group gap="sm" wrap="nowrap">
                              <AppIcon
                                iconName={getIconForKind(kind.code)}
                                size={ICON_SIZES.kindOption}
                              />
                              <Text size="md" fw={isSelected ? 600 : 400}>
                                {kind.name}
                              </Text>

                              {kind.isItMaintenance && (
                                <AppIcon
                                  iconName="Tool"
                                  size={ICON_SIZES.maintenanceBadge}
                                  color="dimmed"
                                />
                              )}

                              {isSelected && (
                                <Box ml="auto">
                                  <AppIcon
                                    iconName="Check"
                                    size={ICON_SIZES.check}
                                    color="var(--mantine-color-blue-6)"
                                  />
                                </Box>
                              )}
                            </Group>
                          </Combobox.Option>
                        );
                      })}
                    </Combobox.Group>
                  );
                })}
              </Combobox.Options>
            </ScrollArea.Autosize>
          </Combobox.Dropdown>
        </Combobox>

        {/* Quick Access Buttons */}
        {quickAccessKinds.length > 0 && !disabled && (
          <Group gap="xs" wrap="wrap">
            {quickAccessKinds.map((kind) => {
              const isSelected =
                kind.id !== undefined && value.includes(Number(kind.id));
              const isDisabledByMax = isAtMax && !isSelected;
              return (
                <Button
                  key={kind.id}
                  variant={isSelected ? "filled" : "light"}
                  size="sm"
                  disabled={isDisabledByMax}
                  leftSection={
                    <AppIcon
                      iconName={getIconForKind(kind.code)}
                      size={ICON_SIZES.quickAccess}
                    />
                  }
                  onClick={() => handleQuickToggle(kind)}
                >
                  {kind.name}
                </Button>
              );
            })}
          </Group>
        )}
      </Stack>
    );
  };